import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { routes } from '../../app/router/routes';
import { getStructuredData } from './structured-data';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  type?: 'website' | 'article' | 'product';
  structuredData?: object;
}

export function SEOHead({
  title,
  description,
  keywords,
  image,
  type = 'website',
  structuredData
}: SEOHeadProps) {
  const location = useLocation();

  // Find the current route configuration
  const currentRoute = routes.find(route => {
    if (route.path === '*') return false;
    if (route.path === '/') return location.pathname === '/';
    return location.pathname === route.path;
  });

  // Use props or fallback to route configuration
  const pageTitle = title || currentRoute?.title || 'EasyBuy General Trading';
  const pageDescription = description || currentRoute?.description || 'Leading supply chain management and global trading solutions';
  const pageKeywords = keywords || currentRoute?.keywords || ['supply chain', 'global trading', 'logistics'];
  const pageImage = image || `${window.location.origin}/og-image.jpg`;
  const canonicalUrl = `${window.location.origin}${location.pathname}`;

  // Get structured data for the current page
  const defaultStructuredData = getStructuredData(location.pathname);
  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords.join(', ')} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="EasyBuy General Trading" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="EasyBuy General Trading" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      {/* Structured Data */}
      {finalStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(finalStructuredData)}
        </script>
      )}
    </Helmet>
  );
}